import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Secondary line under the title */
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  /** Max width class (default: max-w-2xl) */
  maxWidth?: string;
  /** Remove padding from body content (useful for full-bleed content like maps) */
  noPadding?: boolean;
  /** Accessible name for dialogs without a visible `title` */
  "aria-label"?: string;
  /**
   * `center` (default): centred dialog, bottom sheet on mobile.
   * `right`: full-height slide-over from the right (filter panels, detail drawers).
   */
  placement?: "center" | "right";
  /** Extra controls in the title row, left of the close button */
  headerActions?: React.ReactNode;
  /** Floating close button for dialogs without a `title` (titled dialogs always have one) */
  showClose?: boolean;
}

/** Open modals, innermost last, so Escape only closes the topmost one */
const modalStack: symbol[] = [];

/**
 * Centered dialog rendered in a portal. Closes on Escape and backdrop click,
 * locks page scroll, and returns focus to the trigger when it closes.
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  maxWidth = "max-w-2xl",
  noPadding = false,
  "aria-label": ariaLabel,
  placement = "center",
  headerActions,
  showClose = false,
}) => {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const isSheet = placement === "right";
  const panelRef = React.useRef<HTMLDivElement>(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const token = Symbol("modal");
    modalStack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modalStack[modalStack.length - 1] === token) {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      const i = modalStack.indexOf(token);
      if (i !== -1) modalStack.splice(i, 1);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[1100] flex",
        isSheet ? "justify-end" : "items-end justify-center p-0 sm:items-center sm:p-4"
      )}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 animate-fade-in bg-gray-950/50 backdrop-blur-[3px]" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ? undefined : ariaLabel}
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={title && description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          "relative flex w-full flex-col bg-white shadow-overlay outline-none ring-1 ring-gray-950/5",
          isSheet
            ? "h-full animate-sheet-in sm:max-w-md"
            : "max-h-[92vh] animate-dialog-in rounded-t-xl sm:rounded-xl",
          isSheet ? maxWidth !== "max-w-2xl" && maxWidth : maxWidth,
          className
        )}
      >
        {title && (
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 pt-5">
            <div className="min-w-0">
              <h2 id={titleId} className="text-base font-semibold tracking-tight text-gray-900">{title}</h2>
              {description && <p id={descriptionId} className="mt-1 text-sm text-gray-500">{description}</p>}
            </div>
            <div className="-mr-2 -mt-1 flex shrink-0 items-center gap-1">
              {headerActions}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        )}
        {!title && showClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 z-10 rounded-lg bg-white/80 p-2 text-gray-500 backdrop-blur transition-colors hover:bg-gray-100 hover:text-gray-800"
          >
            <X className="size-4" />
          </button>
        )}

        <div
          className={cn(
            "min-h-0 flex-1",
            noPadding ? "flex flex-col overflow-hidden" : "overflow-y-auto px-6 py-5"
          )}
        >
          {children}
        </div>

        {footer && (
          <div className={cn("flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3", !isSheet && "rounded-b-2xl")}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
